import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import ApiInspector from '../../../components/ApiInspector';
import Modal from '../../../components/Modal';
import BopRowActions, { RowIcon } from '../../../components/BopRowActions';
import ComboFiltro from '../../../components/ComboFiltro';
import { apiFetch } from '../../../lib/api';
import { useFiltroSocieta } from '../../../hooks/useFiltroSocieta';
import { usePermesso } from '../../../context/PermessiContext';
import { showToast } from '../../../components/Toast';
import { formatCurrency, formatNumber } from '../../../utils/formatters';
import BopExcelTable, { BopExcelColumn } from '../../../components/BopExcelTable';
import { API_DOCS } from './AccontiPage.api';
import './AccontiPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type StatoProformaAcconto = 'DA_INCASSARE' | 'PAGATA' | 'ANNULLATA';
type OrigineProformaAcconto = 'DA_CONVERSIONE_ORDINE' | 'MANUALE_COMMESSA';

interface ProformaItem {
  id: number;
  numero: string;
  origine: OrigineProformaAcconto;
  stato: StatoProformaAcconto;
  commessaId: number;
  commessa: { numero: string } | null;
  ordineId: number | null;
  acquirenteId: number | null;
  imponibile: string;
  iva: string;
  totale: string;
  clienteRagioneSociale: string | null;
  dataEmissione: string;
  dataPagamento: string | null;
  totaleIncassato: number;
  residuo: number;
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

interface CodiceIvaOption {
  id: number;
  codice: string;
  descrizione: string;
  aliquota: string;
  attivo: boolean;
}

interface PagamentoOption {
  id: number;
  nome: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATO_LABEL: Record<StatoProformaAcconto, string> = {
  DA_INCASSARE: 'Da incassare',
  PAGATA: 'Pagata',
  ANNULLATA: 'Annullata',
};

const STATO_BADGE: Record<StatoProformaAcconto, string> = {
  DA_INCASSARE: 'bop-badge-neutral',
  PAGATA: 'bop-badge-success',
  ANNULLATA: 'bop-badge-neutral',
};

const ORIGINE_LABEL: Record<OrigineProformaAcconto, string> = {
  DA_CONVERSIONE_ORDINE: 'Da ordine',
  MANUALE_COMMESSA: 'Manuale',
};

const ORIGINE_BADGE: Record<OrigineProformaAcconto, string> = {
  DA_CONVERSIONE_ORDINE: 'bop-badge-info',
  MANUALE_COMMESSA: 'bop-badge-neutral',
};

function formatDateIT(s: string | null | undefined): string {
  if (!s) return '—';
  const d = new Date(s);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

function buildAccontiColumns(
  canEdit: boolean,
  onAnnulla: (p: ProformaItem) => void,
): BopExcelColumn<ProformaItem>[] {
  return [
    {
      key: 'numero',
      header: 'Numero',
      accessor: (p) => p.numero,
      filter: { type: 'text' },
      width: '120px',
      render: (p) => <span className="acconti-numero-link">{p.numero}</span>,
    },
    {
      key: 'origine',
      header: 'Origine',
      accessor: (p) => p.origine,
      filter: {
        type: 'enum',
        options: [
          { value: 'DA_CONVERSIONE_ORDINE', label: 'Da ordine' },
          { value: 'MANUALE_COMMESSA',      label: 'Manuale' },
        ],
      },
      width: '120px',
      render: (p) => (
        <span className={`bop-badge ${ORIGINE_BADGE[p.origine]}`}>
          {ORIGINE_LABEL[p.origine]}
        </span>
      ),
    },
    {
      key: 'commessa',
      header: 'Commessa',
      accessor: (p) => p.commessa?.numero || '',
      filter: { type: 'text' },
      width: '120px',
      render: (p) => p.commessa ? (
        <span className="acconti-commessa-link">{p.commessa.numero}</span>
      ) : (
        <span className="bop-text-muted">—</span>
      ),
    },
    {
      key: 'ordineId',
      header: 'Ordine',
      accessor: (p) => p.ordineId,
      filter: { type: 'number' },
      width: '100px',
      render: (p) => p.ordineId ? (
        <span className="acconti-ordine-ref">#{p.ordineId}</span>
      ) : (
        <span className="bop-text-muted">—</span>
      ),
    },
    {
      key: 'cliente',
      header: 'Cliente',
      accessor: (p) => p.clienteRagioneSociale || '',
      filter: { type: 'text' },
      render: (p) => p.clienteRagioneSociale || <span className="bop-text-muted">—</span>,
    },
    {
      key: 'totale',
      header: 'Totale',
      accessor: (p) => p.totale != null ? Number(p.totale) : null,
      filter: { type: 'number' },
      width: '110px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right acconti-totale',
      render: (p) => formatCurrency(Number(p.totale)),
    },
    {
      key: 'totaleIncassato',
      header: 'Incassato',
      accessor: (p) => Number(p.totaleIncassato),
      filter: { type: 'number' },
      width: '110px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right acconti-totale',
      render: (p) => formatCurrency(Number(p.totaleIncassato)),
    },
    {
      key: 'residuo',
      header: 'Residuo',
      accessor: (p) => Number(p.residuo),
      filter: { type: 'number' },
      width: '110px',
      headerClassName: 'bop-text-right',
      cellClassName: 'bop-text-right acconti-totale',
      render: (p) => formatCurrency(Number(p.residuo)),
    },
    {
      key: 'stato',
      header: 'Stato',
      accessor: (p) => p.stato,
      filter: {
        type: 'enum',
        options: [
          { value: 'DA_INCASSARE', label: 'Da incassare' },
          { value: 'PAGATA',       label: 'Pagata' },
          { value: 'ANNULLATA',    label: 'Annullata' },
        ],
      },
      width: '130px',
      render: (p) => (
        <span className={`bop-badge ${STATO_BADGE[p.stato]}`}>
          {STATO_LABEL[p.stato]}
        </span>
      ),
    },
    {
      key: 'dataEmissione',
      header: 'Data emissione',
      accessor: (p) => p.dataEmissione || '',
      filter: { type: 'dateRange' },
      width: '130px',
      render: (p) => formatDateIT(p.dataEmissione),
    },
  ];
  // canEdit/onAnnulla intentionally read via rowActions in the page; declared params keep API stable for future inline use
}

// ── Pagina ────────────────────────────────────────────────────────────────────

export default function AccontiPage() {
  const { societaId, listaSocieta, isDisabled, pronta } = useFiltroSocieta();
  const { can, isSuperadmin } = usePermesso();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Stato locale società (solo SUPERADMIN con "Tutte" può modificarlo)
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri (inizializzati da URL)
  const [stato, setStato] = useState<'' | StatoProformaAcconto>(
    (searchParams.get('stato') as '' | StatoProformaAcconto) || ''
  );
  // Flag "Visualizza annullate": disattivo di default, le proforme ANNULLATA
  // sono nascoste finché non viene attivato.
  const [mostraAnnullate, setMostraAnnullate] = useState(
    searchParams.get('includeAnnullate') === 'true'
  );
  const [origine, setOrigine] = useState<'' | OrigineProformaAcconto>(
    (searchParams.get('origine') as '' | OrigineProformaAcconto) || ''
  );
  const [commessaIdFilter, setCommessaIdFilter] = useState(searchParams.get('commessaId') ?? '');
  const [dataDa, setDataDa] = useState(searchParams.get('dataDa') ?? '');
  const [dataA, setDataA] = useState(searchParams.get('dataA') ?? '');
  const [qInput, setQInput] = useState(searchParams.get('q') ?? '');
  const [q, setQ] = useState(searchParams.get('q') ?? '');

  // Dati
  const [items, setItems] = useState<ProformaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);

  // Modali
  const [modalNuova, setModalNuova] = useState(false);
  const [modalAnnulla, setModalAnnulla] = useState<{ id: number; numero: string } | null>(null);

  const canEdit = can('commerciale.acconti', 'edit');

  // Debounce ricerca testo
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  // Carica lista
  const caricaLista = useCallback(async () => {
    if (!pronta) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (societaIdEff) params.set('societaId', societaIdEff);
      if (stato) params.set('stato', stato);
      if (mostraAnnullate) params.set('includeAnnullate', 'true');
      if (origine) params.set('origine', origine);
      if (commessaIdFilter) params.set('commessaId', commessaIdFilter);
      if (dataDa) params.set('dataDa', dataDa);
      if (dataA) params.set('dataA', dataA);
      if (q) params.set('q', q);

      // @api GET /api/proforme-acconto?societaId={id}&stato={s}&includeAnnullate={bool}&origine={o}&commessaId={cid}&ordineId={oid}&acquirenteId={aid}&dataDa={d}&dataA={d}&q={q} -> {"success":true,"data":[{"id":"number","numero":"string","origine":"string","stato":"string","commessaId":"number","commessa":{"numero":"string"},"ordineId":"number|null","acquirenteId":"number|null","imponibile":"string","iva":"string","totale":"string","clienteRagioneSociale":"string","dataEmissione":"string","dataPagamento":"string|null","totaleIncassato":"number","residuo":"number"}]}
      const r = await apiFetch<{ success: boolean; data: ProformaItem[] }>(
        `/api/proforme-acconto?${params.toString()}`
      );
      setItems(r.data || []);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nel caricamento', 'error');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [pronta, societaIdEff, stato, mostraAnnullate, origine, commessaIdFilter, dataDa, dataA, q]);

  useEffect(() => { caricaLista(); }, [caricaLista]);

  // Sincronizza URL con i filtri
  useEffect(() => {
    const sp = new URLSearchParams();
    if (stato) sp.set('stato', stato);
    if (mostraAnnullate) sp.set('includeAnnullate', 'true');
    if (origine) sp.set('origine', origine);
    if (commessaIdFilter) sp.set('commessaId', commessaIdFilter);
    if (dataDa) sp.set('dataDa', dataDa);
    if (dataA) sp.set('dataA', dataA);
    if (q) sp.set('q', q);
    setSearchParams(sp, { replace: true });
  }, [stato, mostraAnnullate, origine, commessaIdFilter, dataDa, dataA, q, setSearchParams]);

  // Carica commesse per il combo filtro (cambia con societaId effettiva)
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  const pulisciFiltri = () => {
    setStato('');
    setMostraAnnullate(false);
    setOrigine('');
    setCommessaIdFilter('');
    setDataDa('');
    setDataA('');
    setQInput('');
    setQ('');
  };

  // Opzioni per ComboFiltro (id stringa)
  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  const apriDettaglio = (id: number) => {
    navigate(`/commerciale/attivo/acconti/${id}`);
  };

  const columns = useMemo(
    () => buildAccontiColumns(canEdit, (p) => setModalAnnulla({ id: p.id, numero: p.numero })),
    [canEdit],
  );

  return (
    <Layout>
      <div className="acconti-page">
        {/* Header */}
        <div className="bop-page-header">
          <h1 className="bop-page-title">Acconti</h1>
          {canEdit && (
            <button
              className="bop-btn-primary"
              onClick={() => setModalNuova(true)}
              disabled={!societaIdEff && !isSuperadmin}
            >
              + Nuova proforma acconto
            </button>
          )}
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar acconti-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                {isSuperadmin && <option value="">Tutte</option>}
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input acconti-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Numero, cliente..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Stato</label>
            <select
              className="bop-select"
              value={stato}
              onChange={e => setStato(e.target.value as '' | StatoProformaAcconto)}
            >
              <option value="">Tutti</option>
              <option value="DA_INCASSARE">Da incassare</option>
              <option value="PAGATA">Pagata</option>
            </select>
            <label className="acconti-flag-annullate">
              <input
                type="checkbox"
                checked={mostraAnnullate}
                onChange={e => setMostraAnnullate(e.target.checked)}
              />
              <span>Visualizza annullate</span>
            </label>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Origine</label>
            <select
              className="bop-select"
              value={origine}
              onChange={e => setOrigine(e.target.value as '' | OrigineProformaAcconto)}
            >
              <option value="">Tutte</option>
              <option value="DA_CONVERSIONE_ORDINE">Da ordine</option>
              <option value="MANUALE_COMMESSA">Manuale</option>
            </select>
          </div>

          <div className="bop-form-group acconti-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaIdFilter || null}
              onChange={id => setCommessaIdFilter(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Da</label>
            <input
              type="date"
              className="bop-input"
              value={dataDa}
              onChange={e => setDataDa(e.target.value)}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">A</label>
            <input
              type="date"
              className="bop-input"
              value={dataA}
              onChange={e => setDataA(e.target.value)}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Tabella Excel-like */}
        {!pronta ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società per visualizzare gli acconti</div>
          </div>
        ) : (
          <BopExcelTable<ProformaItem>
            data={items}
            loading={loading}
            rowKey={(p) => p.id}
            onRowClick={(p) => apriDettaglio(p.id)}
            initialSort={{ key: 'dataEmissione', dir: 'desc' }}
            emptyState={<div className="bop-empty-state">Nessuna proforma acconto trovata con i filtri attuali.</div>}
            columns={columns}
            rowActions={(p) => (
              <BopRowActions
                actions={[
                  {
                    icon: <RowIcon.Disable />,
                    label: 'Annulla',
                    onClick: () => setModalAnnulla({ id: p.id, numero: p.numero }),
                    variant: 'danger',
                    hidden:
                      !canEdit ||
                      p.origine !== 'MANUALE_COMMESSA' ||
                      p.stato !== 'DA_INCASSARE',
                  },
                ]}
              />
            )}
          />
        )}

        {modalNuova && (
          <ModaleNuovaProforma
            isSuperadmin={isSuperadmin}
            listaSocieta={listaSocieta}
            societaIdEff={societaIdEff}
            onClose={() => setModalNuova(false)}
            onCreated={() => {
              setModalNuova(false);
              caricaLista();
            }}
          />
        )}

        {modalAnnulla && (
          <ModaleAnnullaProforma
            id={modalAnnulla.id}
            numero={modalAnnulla.numero}
            onClose={() => setModalAnnulla(null)}
            onDone={() => {
              setModalAnnulla(null);
              caricaLista();
            }}
          />
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}

// ── Modale: Nuova proforma acconto su commessa ────────────────────────────────

interface ModaleNuovaProps {
  isSuperadmin?: boolean;
  listaSocieta?: { id: number; nome: string }[];
  societaIdEff?: string;
  /** Se valorizzata, la proforma è pre-collegata a questa commessa e i
   *  campi società/commessa sono bloccati (apertura dal tab Acconti). */
  commessaFissa?: { id: number; numero: string; societaId: number };
  onClose: () => void;
  onCreated: () => void;
}

export function ModaleNuovaProforma({
  isSuperadmin = false,
  listaSocieta = [],
  societaIdEff = '',
  commessaFissa,
  onClose,
  onCreated,
}: ModaleNuovaProps) {
  const [societaIdForm, setSocietaIdForm] = useState(
    commessaFissa ? String(commessaFissa.societaId) : societaIdEff || ''
  );
  const [commessaId, setCommessaId] = useState(
    commessaFissa ? String(commessaFissa.id) : ''
  );
  const [imponibile, setImponibile] = useState('');
  const [codiceIvaId, setCodiceIvaId] = useState('');
  const [condizionePagamentoId, setCondizionePagamentoId] = useState('');
  const [descrizione, setDescrizione] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);
  const [codiciIvaOpts, setCodiciIvaOpts] = useState<CodiceIvaOption[]>([]);
  const [pagamentiOpts, setPagamentiOpts] = useState<PagamentoOption[]>([]);

  useEffect(() => {
    if (!societaIdForm) {
      setCommesseOpts([]);
      setCodiciIvaOpts([]);
      setPagamentiOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdForm}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
    // @api GET /api/codici-iva?societaId={id}&attivo=true -> {"codici":[{"id":"number","codice":"string","descrizione":"string","aliquota":"string","attivo":"boolean"}]}
    apiFetch<{ codici: CodiceIvaOption[] }>(`/api/codici-iva?societaId=${societaIdForm}&attivo=true`)
      .then(r => setCodiciIvaOpts(r.codici || []))
      .catch(() => setCodiciIvaOpts([]));
    // @api GET /api/pagamenti?societaId={id} -> {"success":true,"data":[{"id":"number","nome":"string"}],"condizioni":[{"id":"number","nome":"string"}]}
    apiFetch<{ data?: PagamentoOption[]; condizioni?: PagamentoOption[] }>(`/api/pagamenti?societaId=${societaIdForm}`)
      .then(r => setPagamentiOpts(r.data || r.condizioni || []))
      .catch(() => setPagamentiOpts([]));
  }, [societaIdForm]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  const codiciIvaComboItems = codiciIvaOpts.map(c => ({
    id: String(c.id),
    nome: `${c.codice} — ${c.descrizione} (${formatNumber(parseFloat(c.aliquota))}%)`,
  }));

  const pagamentiComboItems = pagamentiOpts.map(p => ({
    id: String(p.id),
    nome: p.nome,
  }));

  const submit = async () => {
    if (!societaIdForm) {
      showToast('Seleziona una società', 'error');
      return;
    }
    if (!commessaId) {
      showToast('Seleziona una commessa', 'error');
      return;
    }
    const impNum = parseFloat(imponibile);
    if (isNaN(impNum) || impNum <= 0) {
      showToast('Imponibile non valido', 'error');
      return;
    }
    if (!codiceIvaId) {
      showToast('Seleziona un codice IVA', 'error');
      return;
    }

    setSaving(true);
    try {
      // @api POST /api/proforme-acconto {"societaId":"number","commessaId":"number","imponibile":"number","codiceIvaId":"number","descrizione":"string|null","note":"string|null","condizionePagamentoId":"number|null"} -> {"success":true,"data":{"id":"number"}}
      await apiFetch<{ success: boolean; data: { id: number } }>(`/api/proforme-acconto`, {
        method: 'POST',
        body: JSON.stringify({
          societaId: parseInt(societaIdForm, 10),
          commessaId: parseInt(commessaId, 10),
          imponibile: impNum,
          codiceIvaId: parseInt(codiceIvaId, 10),
          descrizione: descrizione.trim() || null,
          note: note.trim() || null,
          condizionePagamentoId: condizionePagamentoId
            ? parseInt(condizionePagamentoId, 10)
            : null,
        }),
      });
      showToast('Proforma acconto creata', 'success');
      onCreated();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nella creazione', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Nuova proforma acconto su commessa"
      onClose={onClose}
      footer={
        <>
          <button className="bop-btn-secondary" onClick={onClose} disabled={saving}>
            Annulla
          </button>
          <button className="bop-btn-primary" onClick={submit} disabled={saving}>
            {saving ? 'Salvataggio…' : 'Crea proforma'}
          </button>
        </>
      }
    >
      <div className="acconti-modal-form">
        {!commessaFissa && (isSuperadmin || listaSocieta.length > 1) && (
          <div className="bop-form-group">
            <label className="bop-label">Società *</label>
            <select
              className="bop-select"
              value={societaIdForm}
              onChange={e => {
                setSocietaIdForm(e.target.value);
                setCommessaId('');
              }}
            >
              <option value="">— Seleziona —</option>
              {listaSocieta.map(s => (
                <option key={s.id} value={String(s.id)}>{s.nome}</option>
              ))}
            </select>
          </div>
        )}

        <div className="bop-form-group">
          <label className="bop-label">Commessa *</label>
          {commessaFissa ? (
            <input className="bop-input" value={commessaFissa.numero} disabled readOnly />
          ) : (
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdForm}
            />
          )}
        </div>

        <div className="bop-form-row">
          <div className="bop-form-group">
            <label className="bop-label">Imponibile *</label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="bop-input"
              value={imponibile}
              onChange={e => setImponibile(e.target.value)}
            />
          </div>
          <div className="bop-form-group">
            <label className="bop-label">Codice IVA *</label>
            <ComboFiltro
              label="codice IVA"
              value={codiceIvaId || null}
              onChange={id => setCodiceIvaId(id || '')}
              items={codiciIvaComboItems}
              disabled={!societaIdForm}
            />
          </div>
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Condizione pagamento</label>
          <ComboFiltro
            label="condizione pagamento"
            value={condizionePagamentoId || null}
            onChange={id => setCondizionePagamentoId(id || '')}
            items={pagamentiComboItems}
            disabled={!societaIdForm}
          />
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Descrizione</label>
          <input
            className="bop-input"
            value={descrizione}
            onChange={e => setDescrizione(e.target.value)}
          />
        </div>

        <div className="bop-form-group">
          <label className="bop-label">Note</label>
          <textarea
            className="bop-input acconti-modal-textarea"
            value={note}
            onChange={e => setNote(e.target.value)}
            rows={3}
          />
        </div>
      </div>
    </Modal>
  );
}

// ── Modale: Annulla proforma ──────────────────────────────────────────────────

interface ModaleAnnullaProps {
  id: number;
  numero: string;
  onClose: () => void;
  onDone: () => void;
}

function ModaleAnnullaProforma({ id, numero, onClose, onDone }: ModaleAnnullaProps) {
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!motivo.trim()) {
      showToast('Inserisci il motivo dell\'annullamento', 'error');
      return;
    }
    setSaving(true);
    try {
      // @api POST /api/proforme-acconto/:id/annulla {"motivoAnnullamento":"string"} -> {"success":true}
      await apiFetch<{ success: boolean }>(`/api/proforme-acconto/${id}/annulla`, {
        method: 'POST',
        body: JSON.stringify({ motivoAnnullamento: motivo.trim() }),
      });
      showToast('Proforma annullata', 'success');
      onDone();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Errore nell\'annullamento', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={`Annulla proforma ${numero}`}
      onClose={onClose}
      footer={
        <>
          <button className="bop-btn-secondary" onClick={onClose} disabled={saving}>
            Annulla
          </button>
          <button className="bop-btn-danger" onClick={submit} disabled={saving}>
            {saving ? 'Salvataggio…' : 'Conferma annullamento'}
          </button>
        </>
      }
    >
      <div className="acconti-modal-form">
        <div className="bop-form-group">
          <label className="bop-label">Motivo annullamento *</label>
          <textarea
            className="bop-input acconti-modal-textarea"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={4}
            placeholder="Indica il motivo dell'annullamento della proforma"
          />
        </div>
      </div>
    </Modal>
  );
}
